import type { IconName } from "@bundle/tokens";
import { Select as RadixSelect } from "radix-ui";
import { useId } from "react";
import { usePrimitiveT } from "../i18n/I18nProvider.tsx";
import type { ControlName } from "./Checkbox.tsx";
import { Icon } from "./Icon.tsx";
import { cx } from "./tokens.ts";

export type SelectOption<K extends string = string> = { value: K; label: string; icon?: IconName; disabled?: boolean };

export type SelectProps<K extends string = string> = ControlName & {
  /** The chosen option; undefined shows the placeholder. */
  value: K | undefined;
  onChange: (value: K) => void;
  options: SelectOption<K>[];
  /** Shown while nothing is chosen (default "Choose…"). */
  placeholder?: string;
  /** A hint under the field; `error` replaces it while set. */
  helperText?: string;
  /** Validation message under the field; the field is marked invalid. */
  error?: string;
  disabled?: boolean;
  /** The field; each option is `<testID>-option-<value>`. */
  testID: string;
};

/**
 * Pick one value from a list, in a field that looks like `TextField` (label, helper, error). Web: Radix Select;
 * iOS `Picker(.menu)` in a field row; Android `ExposedDropdownMenuBox`.
 */
export function Select<K extends string>({ value, onChange, options, label, accessibilityLabel, placeholder, helperText, error, disabled, testID }: SelectProps<K>) {
  const t = usePrimitiveT();
  const id = useId();
  const note = error ?? helperText;
  const noteId = `${id}-note`;
  return (
    <div className={cx("b-field", "b-field-outlined", disabled === true && "b-field-disabled", error !== undefined && "b-field-error")}>
      {label === undefined ? null : (
        <label className="b-field-label" htmlFor={id}>
          {label}
        </label>
      )}
      <RadixSelect.Root value={value ?? ""} onValueChange={(v) => onChange(v as K)} disabled={disabled}>
        <RadixSelect.Trigger
          id={id}
          className="b-field-box b-select-trigger"
          aria-label={label === undefined ? accessibilityLabel : undefined}
          aria-invalid={error === undefined ? undefined : true}
          aria-describedby={note === undefined ? undefined : noteId}
          data-testid={testID}
        >
          <span className="b-select-value">
            <RadixSelect.Value placeholder={placeholder ?? t("primitive.select.placeholder")} />
          </span>
          <RadixSelect.Icon className="b-select-icon">
            <Icon name="chevronDown" size="icon.sm" />
          </RadixSelect.Icon>
        </RadixSelect.Trigger>
        <RadixSelect.Portal>
          <RadixSelect.Content className="b-menu b-select-content" position="popper" sideOffset={6} collisionPadding={12}>
            <RadixSelect.Viewport>
              {options.map((o) => (
                <RadixSelect.Item key={o.value} value={o.value} disabled={o.disabled} className="b-menu-item" data-testid={`${testID}-option-${o.value}`}>
                  {o.icon === undefined ? null : <Icon name={o.icon} size="icon.sm" />}
                  <span className="b-menu-label">
                    <RadixSelect.ItemText>{o.label}</RadixSelect.ItemText>
                  </span>
                  <RadixSelect.ItemIndicator className="b-menu-check">
                    <Icon name="check" size="icon.sm" />
                  </RadixSelect.ItemIndicator>
                </RadixSelect.Item>
              ))}
            </RadixSelect.Viewport>
          </RadixSelect.Content>
        </RadixSelect.Portal>
      </RadixSelect.Root>
      {note === undefined ? null : (
        <span id={noteId} className={error === undefined ? "b-field-note" : "b-field-note b-field-note-error"}>
          {note}
        </span>
      )}
    </div>
  );
}
